import { Container, Typography, Paper, Stack, Alert, Box, Divider, Chip } from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useMe } from '../../context/MeContext';

const money = (amount, currency) => {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency || 'INR'} ${n}`;
  }
};

const Step = ({ children }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ mt: '2px' }} />
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Stack>
);

const Billing = () => {
  const { billing } = useMe();
  const price = billing?.assessmentPrice;
  const currency = billing?.currency || 'INR';
  const exempt = !!billing?.isExempt;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Billing</Typography>

      {billing?.isSuspended && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This clinic is currently suspended. Please contact Vqura support to restore access.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
          <PaymentsIcon color="primary" />
          <Typography variant="h6">Pay per assessment</Typography>
          {exempt && <Chip size="small" label="Not charged" color="default" />}
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {exempt ? (
          <Typography color="text.secondary">
            This clinic is not billed per assessment.
          </Typography>
        ) : (
          <Box>
            <Typography variant="overline" color="text.secondary" display="block">
              Price per assessment
            </Typography>
            <Typography variant="h3" fontWeight={800}>{money(price, currency)}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This price is set for your clinic by Vqura. To change it, contact Vqura support.
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>How billing works</Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={1.5}>
          <Step>You pay once per assessment, before a clinician starts scoring a child.</Step>
          <Step>One payment covers the full screening across all developmental domains.</Step>
          <Step>You can score across multiple sittings and resume any time before finishing.</Step>
          <Step>Each periodic reassessment of the same child is a new, separately-paid assessment.</Step>
        </Stack>
      </Paper>
    </Container>
  );
};

export default Billing;
